import { nativeNotifyPull } from './native-notify'
import { nativeRecorderAvailable } from './native-recorder'

// ============================================================================
// A REMINDER MADE NOW RINGS NOW — the two halves of "the phone may not know yet".
//
// WHY (owner, 2026-09-25 evening). The installed phone app learns what to ring by pulling
// `/api/notifications/upcoming` every 15 minutes, plus when it comes to the foreground or leaves
// it (native-notify.ts). He made a reminder on the DESKTOP at 8:10 PM for 8:12 PM; the phone had
// no pull in that window, so it never rang. A reminder made hours earlier rang to the minute.
//
//   1. INSIDE THE PHONE APP, a write that can ring asks the phone to check with LifeOS at once
//      (`armPhoneAlarmsNow`). The data hooks call it after the write succeeds — one place, not
//      every component — fire-and-forget: it never blocks, never rejects, never logs.
//   2. EVERYWHERE ELSE, a reminder set for under 15 minutes from now says so, in one quiet line
//      (`phoneMayMissIt` + components/tasks/PhoneMayMissNote.tsx). Inside the app there is no
//      note, because (1) already covered it.
//
// "INSIDE THE APP" is `nativeRecorderAvailable()`: the Tauri bridge is only ever on this page in
// the Android build (apps/desktop/src-tauri/capabilities has android.json and nothing else).
// ============================================================================

/** How often the phone checks on its own — AlertScheduler.kt's periodic pull. */
export const PHONE_PULL_EVERY_MS = 15 * 60_000

/** The one sentence, verbatim wherever a near-term reminder is set outside the app. */
export const PHONE_MAY_MISS_NOTE =
  'Your phone checks for new reminders every 15 minutes — this one may only ring here.'

/**
 * Ask the phone to re-read its alerts now. A no-op in a browser tab; inside the app, one
 * `notifypull`, whose answer (and any failure) is dropped on the floor — the 15-minute pull is the
 * fallback, and a write that already succeeded must not look like it failed because the phone's
 * courtesy check did.
 */
export function armPhoneAlarmsNow(): void {
  try {
    if (!nativeRecorderAvailable()) return
    void nativeNotifyPull().then(noop, noop)
  } catch {
    // A bridge getter that throws is "no phone", not an error worth a word.
  }
}

function noop(): void {}

/**
 * Should the "may only ring here" line show?
 *
 * `reminderAtMs` is the instant being set; `nowMs` is the APP clock (`useClock` →
 * `clockInstant`), minute-grained, so it is the start of this minute. The window is
 * `[now, now + 15 min)`: a time already past is not a reminder anyone is waiting on, and one 15
 * minutes or more out is inside at least one periodic pull. `inNativeShell` answers with no line —
 * the immediate pull above is that surface's answer.
 */
export function phoneMayMissIt(
  reminderAtMs: number | null | undefined,
  nowMs: number | null | undefined,
  inNativeShell: boolean,
): boolean {
  if (inNativeShell) return false
  if (reminderAtMs == null || nowMs == null) return false
  if (!Number.isFinite(reminderAtMs) || !Number.isFinite(nowMs)) return false
  const ahead = reminderAtMs - nowMs
  return ahead >= 0 && ahead < PHONE_PULL_EVERY_MS
}
